<template>
  <ToastRoot
    :open="open"
    @update:open="(value) => $emit('update:open', value)"
    :type="typeof notification.progress === 'number' ? 'background' : 'foreground'"
    class="card md:card-side max-w-full shadow"
    :class="[widthClass, notificationClass]"
    :duration="duration"
  >
    <figure v-if="notification.image" class="w-1/3 shrink-0 grow-0">
      <img :src="notification.image" :alt="notification.imageAlt" />
    </figure>
    <div class="card-body p-4" :class="{ 'pb-6': typeof notification.progress === 'number' }">
      <ToastTitle class="card-title">{{ notification.title }}</ToastTitle>
      <ToastDescription as="p">
        {{ notification.message }}
        <div class="mt-2 text-xl font-bold" v-if="notification.extra">
          {{ notification.extra }}
        </div>
      </ToastDescription>
      <div class="card-actions justify-end" v-if="notification.actions?.length">
        <ToastAction
          v-for="action of notification.actions"
          :key="action.text"
          as-child
          :alt-text="action.text"
        >
          <button class="btn" @click="action.onClick">
            <FontAwesomeIcon v-if="action.icon" :icon="action.icon" />
            {{ action.text }}
          </button>
        </ToastAction>
      </div>
    </div>
    <progress
      v-if="typeof notification.progress === 'number'"
      class="progress absolute right-0 bottom-0 left-0 w-full"
      :class="progressClass"
      :value="notification.progress > 0 ? notification.progress : undefined"
      :max="100"
    />
    <ToastClose v-if="closable" class="btn btn-sm btn-circle btn-ghost absolute top-1 right-1">
      <FontAwesomeIcon :icon="faTimes" />
    </ToastClose>
  </ToastRoot>
</template>

<script setup lang="ts">
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { ToastAction, ToastClose, ToastDescription, ToastRoot, ToastTitle } from 'reka-ui';
import { computed } from 'vue';
import type { Notification } from '../stores/notificationStore';

const props = withDefaults(
  defineProps<{
    notification: Notification;
    open: boolean;
    duration?: number;
    closable?: boolean;
    widthClass?: string;
  }>(),
  { closable: true, widthClass: 'w-96' },
);

defineEmits<{ 'update:open': [boolean] }>();

const notificationClass = computed(() => getNotificationClass(props.notification.type));
const progressClass = computed(() => getProgressClass(props.notification.type));

function getNotificationClass(type: string | undefined) {
  switch (type) {
    case 'primary':
      return 'bg-primary text-primary-content';
    case 'secondary':
      return 'bg-secondary text-secondary-content';
    case 'info':
      return 'bg-info text-info-content';
    case 'success':
      return 'bg-success text-success-content';
    case 'accent':
      return 'bg-accent text-accent-content';
    case 'warning':
      return 'bg-warning text-warning-content';
    case 'error':
      return 'bg-error text-error-content';
    case 'neutral':
    default:
      return 'bg-neutral text-neutral-content';
  }
}

function getProgressClass(type: string | undefined) {
  switch (type) {
    case 'primary':
      return 'progress-primary';
    case 'secondary':
      return 'progress-secondary';
    case 'info':
      return 'progress-info';
    case 'success':
      return 'progress-success';
    case 'accent':
      return 'progress-accent';
    case 'warning':
      return 'progress-warning';
    case 'error':
      return 'progress-error';
    case 'neutral':
    default:
      return 'progress-neutral';
  }
}
</script>
